import React from 'react';

export type ButtonVariant =
  | 'primary'
  | 'system'
  | 'admin'
  | 'adminWarm'
  | 'adminCool'
  | 'packing'
  | 'pickup';
type ButtonSize = 's' | 'm' | 'l';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  children: React.ReactNode;
}

const VARIANT_CLASSES: Record<
  Exclude<ButtonVariant, 'system' | 'primary'>,
  string
> = {
  admin: 'border-[0.8px] border-neutral-200 bg-white text-neutral-500',
  adminWarm:
    'border-[0.8px] border-neutral-300 bg-primary-200 text-neutral-300',
  adminCool:
    'border-[0.8px] border-neutral-300 bg-secondary-blue-100 text-neutral-300',
  packing:
    'border border-packing-600 bg-packing-600 text-white hover:bg-packing-500 active:bg-packing-700 disabled:cursor-not-allowed disabled:border-neutral-100 disabled:bg-neutral-100 disabled:text-neutral-300',
  pickup:
    'border border-pickup-600 bg-pickup-600 text-white hover:bg-pickup-500 active:bg-pickup-700 disabled:cursor-not-allowed disabled:border-neutral-100 disabled:bg-neutral-100 disabled:text-neutral-300',
};

const PRIMARY_CLASSES = {
  m: 'border-[0.8px] border-primary-500 bg-primary-500 text-white hover:border-primary-600 hover:bg-primary-600 active:border-primary-700 active:bg-primary-700 disabled:cursor-not-allowed disabled:border-neutral-200 disabled:bg-neutral-200 disabled:text-neutral-500',
  l: 'border border-primary-600 bg-primary-600 text-white hover:border-primary-700 hover:bg-primary-700 active:border-primary-800 active:bg-primary-800 disabled:cursor-not-allowed disabled:border-neutral-200 disabled:bg-neutral-200 disabled:text-neutral-500',
};

const SYSTEM_CLASSES = {
  s: 'border-[0.8px] border-neutral-300 bg-white text-neutral-400 hover:border-primary-600 hover:bg-primary-200 hover:text-primary-600',
  l: 'border border-primary-600 text-primary-600 hover:bg-primary-200',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  s: 'h-10 px-5 text-label',
  m: 'h-12 px-5 text-button',
  l: 'px-5 py-5 text-base font-semibold leading-[18px] tracking-[0.01em]',
};

const getVariantClass = (variant: ButtonVariant, size: ButtonSize): string => {
  if (variant === 'system') {
    return size === 's' ? SYSTEM_CLASSES.s : SYSTEM_CLASSES.l;
  }

  if (variant === 'primary') {
    return size === 'l' ? PRIMARY_CLASSES.l : PRIMARY_CLASSES.m;
  }

  return VARIANT_CLASSES[variant];
};

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'm',
  fullWidth = false,
  className = '',
  type,
  children,
  ...rest
}) => (
  <button
    type={type === 'submit' ? 'submit' : 'button'}
    className={`flex items-center justify-center rounded-lg ${getVariantClass(variant, size)} ${SIZE_CLASSES[size]} ${fullWidth ? 'w-full' : ''} ${className}`}
    {...rest}
  >
    {children}
  </button>
);

export default Button;
